import { Injectable } from '@angular/core';
import { OrderformFascade } from '../fascades/orderform.fascade';
import { firstValueFrom } from 'rxjs';
import { getOrderformSdk } from '@ds24/orderform-sdk';

declare const grecaptcha: any;

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private initialized = false;
  private _scriptLoaded = false;

  constructor(private orderformFascade: OrderformFascade) {}

  /**
   * Loads the Google reCAPTCHA Enterprise script dynamically.
   */
  loadScript(siteKey: string): Promise<void> {
    if (this._scriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this._scriptLoaded = true;
        resolve();
      };
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
  }

  /**
   * Executes the reCAPTCHA challenge and returns the token.
   */
  async execute(siteKey: string, action: string): Promise<string> {
    if (!this._scriptLoaded) {
      await this.loadScript(siteKey);
    }

    return new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(async () => {
        try {
          const token = await grecaptcha.enterprise.execute(siteKey, { action });
          resolve(token);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  async init() {
    if (this.initialized) return;
    this.initialized = true;

    const config = await firstValueFrom(this.orderformFascade.config$);
    const siteKey = config.recaptcha_site_key;

    if (!siteKey) return;

    this.loadScript(siteKey);

    getOrderformSdk().hooks.beforeSubmit.subscribe(async () => {
      try {
        const token = await this.execute(siteKey, 'order_form');

        // Inject into order data
        this.orderformFascade.setOrderStore({
          // @ts-ignore - dynamic property
          'g-recaptcha-response': token,
        });

        return { valid: true };
      } catch (e) {
        return { valid: false, error: 'Recaptcha failed' };
      }
    });
  }
}
